import { checkExactMathAnswer, exactAnswerPreview } from "../../../../utils/exactMathAnswer";
import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import RootFractionInput from "../../../../components/inputs/RootFractionInput";

const pick = values => values[Math.floor(Math.random() * values.length)];
function gcd(a, b) { while (b) [a, b] = [b, a % b]; return Math.abs(a) || 1; }
function f(num, den = 1) { const d = gcd(num, den); return { num: num / d, den: den / d }; }
const multiply = (a, b) => f(a.num * b.num, a.den * b.den);
const frac = a => a.den === 1 ? String(a.num) : `${a.num < 0 ? "-" : ""}\\frac{${Math.abs(a.num)}}{${a.den}}`;
const coefficient = () => f(pick([1, 2, 3, 4, 5, 7, 8]), pick([1, 2, 3, 4, 5, 6]));
function term(a, r) {
  if (!a.num) return "0";
  if (r === 1) return frac(a);
  const sign = a.num < 0 ? "-" : "";
  const n = Math.abs(a.num) === 1 ? "" : Math.abs(a.num);
  return a.den === 1 ? `${sign}${n}\\sqrt{${r}}` : `${sign}\\frac{${n}\\sqrt{${r}}}{${a.den}}`;
}
function simplifyRoot(n) {
  let factor = 1;
  for (let k = 2; k * k <= n; k++) if (n % (k * k) === 0) factor = k;
  return { factor, remainder: n / (factor * factor) };
}

export function generateTask(options = {}) {
  const type = options.type === "RootCoefficientProductTask" ? options.type : "RootLikeTermsAndScalarTask";
  const p = pick([2, 3, 5, 6, 7, 10, 11, 13]);
  let a = coefficient(), b = coefficient();
  let operation, q = p, result, remainder = p, factor = 1, scalarLatex = "";
  if (type === "RootCoefficientProductTask") {
    operation = "product";
    q = pick([2, 3, 5, 6, 7, 10, 11, 13]);
    if (Math.random() < 0.25) a = f(-a.num, a.den);
    const root = simplifyRoot(p * q);
    factor = root.factor; remainder = root.remainder;
    result = multiply(multiply(a, b), f(factor));
  } else {
    operation = options.operation || pick(["add", "subtract", "scalar"]);
    if (operation === "scalar") {
      a = f(pick([-6, -5, -3, 2, 4, 5]));
      if (Math.random() < 0.5) {
        b = f(pick([-4, -2, 1, 3, 7, 12]), 10);
        scalarLatex = String(b.num / b.den).replace(".", "{,}");
      }
      result = multiply(a, b);
    } else {
      if (operation === "subtract" && a.num * b.den === b.num * a.den) b = f(b.num + b.den, b.den);
      result = f(a.num * b.den + (operation === "subtract" ? -1 : 1) * b.num * a.den, a.den * b.den);
    }
  }
  const factorLatex = (number, text) => number.num < 0 ? "\\left(" + text + "\\right)" : text;
  const expressionLatex = operation === "product"
    ? factorLatex(a, term(a, p)) + "\\cdot " + factorLatex(b, term(b, q))
    : operation === "scalar" ? factorLatex(a, term(a, p)) + "\\cdot " + factorLatex(b, scalarLatex || frac(b))
    : term(a, p) + (operation === "subtract" ? " - " : " + ") + term(b, p);
  return { type, data: { p, q, a, b, operation, factor, remainder, result, expressionLatex, answerLatex: term(result, remainder) } };
}
export const generateTasks = () => [generateTask(), generateTask({ type: "RootCoefficientProductTask" })];
export function renderTask({ data }) { return <div><p>Zapisz wyrażenie w jak najprostszej postaci.</p><BlockMath math={data.expressionLatex} /></div>; }
export function renderInput(task, answer, onChange, disabled) { return <RootFractionInput previewFormatter={exactAnswerPreview} value={answer} onChange={onChange} disabled={disabled} label="wynik" />; }
export function checkAnswer({ data }, answer) {
  return checkExactMathAnswer(answer, data.result, data.remainder);
}
export function renderAnswer({ data }) { return <InlineMath math={data.answerLatex} />; }
export function renderUserAnswer(answer) { const raw = typeof answer === "string" ? answer : answer?.raw; return raw ? (exactAnswerPreview(raw) ? <InlineMath math={exactAnswerPreview(raw)} /> : <span>{raw}</span>) : <i>(brak odpowiedzi)</i>; }
export function renderExplanation({ data: x }) {
  const op = x.operation === "subtract" ? "-" : "+";
  return <div>
    <p>{x.operation === "product" ? "Mnożymy współczynniki oraz pierwiastki, a następnie wyłączamy czynnik spod pierwiastka i skracamy ułamek." : x.operation === "scalar" ? "Mnożymy współczynnik przed pierwiastkiem przez podaną liczbę. Liczbę dziesiętną zamieniamy na ułamek zwykły." : "Pierwiastki są takie same, więc dodajemy lub odejmujemy ich współczynniki, sprowadzając je do wspólnego mianownika."}</p>
    <BlockMath math={x.operation === "product"
      ? `${x.expressionLatex} = \\left(${frac(x.a)}\\right)\\cdot\\left(${frac(x.b)}\\right)\\cdot\\sqrt{${x.p}\\cdot${x.q}} = ${term(multiply(x.a, x.b), x.p * x.q)} = ${x.answerLatex}`
      : x.operation === "scalar"
      ? `${x.expressionLatex} = \\left(${frac(x.a)}\\right)\\cdot\\left(${frac(x.b)}\\right)\\cdot\\sqrt{${x.p}} = ${x.answerLatex}`
      : `${x.expressionLatex} = \\left(${frac(x.a)} ${op} ${frac(x.b)}\\right)\\sqrt{${x.p}} = ${x.answerLatex}`} />
  </div>;
}
const adapter = { generateTask, renderTask, renderInput, checkAnswer, renderAnswer, renderUserAnswer, renderExplanation };
export const registry = { RootLikeTermsAndScalarTask: adapter, RootCoefficientProductTask: adapter };
